/**
 * Admin Level Dialog Component
 * 
 * Dialog for creating or editing admin levels.
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Shield, Users, Building2, MapPin, AlertCircle, CheckCircle2, Info, GripVertical } from "lucide-react";
import type { AdminLevelItem, AdminLevelType, AdminPrivilege } from "@/services/adminService";
import { cn } from "@/lib/utils";
import { useCreateAdminLevel, useUpdateAdminLevel } from "@/components/dashboard/hooks/useAdminLevels";

interface AdminLevelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  adminLevel?: AdminLevelItem | null;
  onSuccess?: () => void;
}

export function AdminLevelDialog({ open, onOpenChange, adminLevel, onSuccess }: AdminLevelDialogProps) {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [level, setLevel] = React.useState<AdminLevelType>("nasional");
  const [order, setOrder] = React.useState<number>(0);
  const [orderInput, setOrderInput] = React.useState<string>("0");
  const [selectedPrivileges, setSelectedPrivileges] = React.useState<AdminPrivilege[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { createAdminLevel, loading: creating } = useCreateAdminLevel();
  const { updateAdminLevel, loading: updating } = useUpdateAdminLevel();

  // Dragging state
  const [isDragging, setIsDragging] = React.useState(false);
  const [position, setPosition] = React.useState<{ x: number; y: number } | null>(null);
  const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);

  const isEdit = !!adminLevel;

  // Reset form when dialog opens/closes or adminLevel changes
  React.useEffect(() => {
    if (open) {
      if (adminLevel) {
        setName(adminLevel.name);
        setDescription(adminLevel.description || "");
        setLevel(adminLevel.level);
        setOrder(adminLevel.order);
        setOrderInput(adminLevel.order.toString());
        setSelectedPrivileges(adminLevel.defaultPrivileges || []);
      } else {
        setName("");
        setDescription("");
        setLevel("nasional");
        setOrder(0);
        setOrderInput("0");
        setSelectedPrivileges([]);
      }
      setError(null);
      // Reset position when dialog opens
      setPosition(null);
    }
  }, [open, adminLevel]);

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!headerRef.current || !dialogRef.current) return;
    
    // Prevent text selection during drag
    e.preventDefault();
    
    const rect = dialogRef.current.getBoundingClientRect();
    const currentPosition = position || {
      x: rect.left,
      y: rect.top,
    };
    
    setDragOffset({
      x: e.clientX - currentPosition.x,
      y: e.clientY - currentPosition.y,
    });
    
    // Set initial position if not already set
    if (!position) {
      setPosition({
        x: rect.left,
        y: rect.top,
      });
    }
    
    setIsDragging(true);
  };

  // Handle drag
  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dialogRef.current) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Constrain to viewport
      const maxX = window.innerWidth - dialogRef.current.offsetWidth;
      const maxY = window.innerHeight - dialogRef.current.offsetHeight;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Handle privilege toggle
  const handlePrivilegeToggle = (privilege: AdminPrivilege, checked: boolean | "indeterminate") => {
    if (checked === true) {
      setSelectedPrivileges([...selectedPrivileges, privilege]);
    } else {
      setSelectedPrivileges(selectedPrivileges.filter(p => p !== privilege));
    }
  };

  // Privilege options with labels
  const privilegeOptions: { value: AdminPrivilege; label: string; description: string }[] = [
    { value: "manage_users", label: "Kelola User", description: "Mengelola data anggota" },
    {
      value: "manage_user_reset_password",
      label: "Reset password anggota",
      description: "Sub-hak: reset password dari daftar anggota (admin)",
    },
    {
      value: "manage_user_set_email",
      label: "Set email anggota",
      description: "Sub-hak: ubah email anggota dari daftar anggota (admin)",
    },
    { value: "manage_locations", label: "Kelola Lokasi", description: "Mengelola data provinsi dan kota" },
    { value: "manage_payments", label: "Kelola Pembayaran", description: "Mengelola transaksi pembayaran" },
    { value: "manage_blocks", label: "Kelola Blokir", description: "Mengelola penangguhan anggota" },
    { value: "manage_silentkey", label: "Silent Key", description: "Mengelola status Silent Key anggota" },
    { value: "manage_jabatan", label: "Kelola Jabatan", description: "Mengelola jabatan dan penugasan" },
    { value: "manage_admin_assignments", label: "Kelola Penugasan Admin", description: "Mengelola penugasan admin" },
    { value: "view_reports", label: "Lihat Laporan", description: "Melihat laporan dan statistik" },
    { value: "manage_transactions", label: "Kelola Transaksi", description: "Mengelola riwayat transaksi" },
    {
      value: "manage_transactions_plan",
      label: "Kelola Rencana Transaksi",
      description: "Akses riwayat transaksi (rencana)",
    },
    {
      value: "create_transaction_plan",
      label: "Buat Rencana Transfer",
      description: "Unggah dan konfirmasi rencana transfer",
    },
    {
      value: "manage_historical_transfer",
      label: "Riwayat Transfer",
      description: "Melihat riwayat transfer",
    },
    {
      value: "manage_transfer_plans",
      label: "Kelola Daftar Rencana",
      description: "Daftar, detail, dan hapus rencana transfer",
    },
    {
      value: "create_transaction_bank_account",
      label: "Tambah Rekening Bank",
      description: "Menambah rekening bank untuk transaksi keuangan",
    },
  ];

  const getLevelConfig = (level: AdminLevelType) => {
    switch (level) {
      case "nasional":
        return {
          label: "Nasional",
          icon: Users,
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-950/20",
          borderColor: "border-blue-200 dark:border-blue-800",
        };
      case "province":
        return {
          label: "Provinsi",
          icon: Building2,
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-950/20",
          borderColor: "border-green-200 dark:border-green-800",
        };
      case "city":
        return {
          label: "Kota/Kabupaten",
          icon: MapPin,
          color: "text-purple-600 dark:text-purple-400",
          bgColor: "bg-purple-50 dark:bg-purple-950/20",
          borderColor: "border-purple-200 dark:border-purple-800",
        };
    }
  };

  const levelConfig = getLevelConfig(level);
  const LevelIcon = levelConfig.icon;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!name.trim()) {
        setError("Nama admin level harus diisi");
        setLoading(false);
        return;
      }

      if (order < 0) {
        setError("Urutan harus berupa angka positif");
        setLoading(false);
        return;
      }

      const payload = {
        name: name.trim(),
        description: description?.trim() || undefined,
        level,
        order,
        defaultPrivileges: selectedPrivileges,
      };

      if (isEdit && adminLevel) {
        await updateAdminLevel(adminLevel._id, payload);
      } else {
        await createAdminLevel(payload);
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal menyimpan admin level");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = name.trim().length > 0 && order >= 0;
  const isLoading = loading || creating || updating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={dialogRef}
        className={cn(
          "sm:max-w-[700px] max-h-[90vh] overflow-y-auto",
          isDragging && "cursor-grabbing",
          position && "translate-x-0 translate-y-0"
        )}
        style={
          position
            ? {
                left: `${position.x}px`,
                top: `${position.y}px`,
                transform: "none",
              }
            : undefined
        }
      >
        <DialogHeader className="space-y-3 pb-4 border-b">
          <div
            ref={headerRef}
            className={cn(
              "flex items-center gap-3 cursor-grab select-none",
              isDragging && "cursor-grabbing"
            )}
            onMouseDown={handleMouseDown}
          >
            <div className={cn(
              "p-2 rounded-lg",
              levelConfig.bgColor,
              levelConfig.borderColor,
              "border"
            )}>
              <Shield className={cn("h-5 w-5", levelConfig.color)} />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                {isEdit ? "Edit Admin Level" : "Tambah Admin Level Baru"}
                <GripVertical className="h-4 w-4 text-muted-foreground opacity-50" />
              </DialogTitle>
              <DialogDescription className="text-sm mt-1">
                {isEdit
                  ? "Ubah informasi admin level yang ada di sistem"
                  : "Tambahkan admin level baru sebagai template untuk penugasan admin"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-5">
            {/* Nama Admin Level */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-semibold flex items-center gap-2">
                Nama Admin Level
                <span className="text-red-500">*</span>
                {name.trim() && (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                )}
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Contoh: Admin Nasional, Admin Provinsi"
                required
                disabled={isLoading}
                className="h-11 text-base"
              />
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Info className="h-3 w-3" />
                Nama admin level yang akan ditampilkan di sistem
              </p>
            </div>

            {/* Deskripsi */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-semibold">
                Deskripsi
                <span className="text-muted-foreground font-normal ml-2">(Opsional)</span>
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Masukkan deskripsi admin level untuk memberikan informasi lebih detail..."
                rows={4}
                disabled={isLoading}
                className="resize-none text-base"
              />
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Info className="h-3 w-3" />
                Deskripsi membantu menjelaskan fungsi dan tanggung jawab admin level
              </p>
            </div>

            {/* Level Selection */}
            <div className="space-y-2">
              <Label htmlFor="level" className="text-sm font-semibold flex items-center gap-2">
                Level Admin
                <span className="text-red-500">*</span>
              </Label>
              <Select
                value={level}
                onValueChange={(value) => setLevel(value as AdminLevelType)}
                disabled={isLoading}
              >
                <SelectTrigger id="level" className="h-11 text-base">
                  <SelectValue placeholder="Pilih level admin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nasional">
                    <div className="flex items-center gap-2 py-1">
                      <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span>Nasional</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="province">
                    <div className="flex items-center gap-2 py-1">
                      <Building2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <span>Provinsi</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="city">
                    <div className="flex items-center gap-2 py-1">
                      <MapPin className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                      <span>Kota/Kabupaten</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              {/* Level Preview */}
              <div className={cn(
                "p-3 rounded-lg border flex items-center gap-3",
                levelConfig.bgColor,
                levelConfig.borderColor
              )}>
                <LevelIcon className={cn("h-5 w-5 shrink-0", levelConfig.color)} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{levelConfig.label}</p>
                  <p className="text-xs text-muted-foreground">
                    Level untuk {levelConfig.label.toLowerCase()}
                  </p>
                </div>
              </div>
            </div>

            {/* Urutan */}
            <div className="space-y-2">
              <Label htmlFor="order" className="text-sm font-semibold flex items-center gap-2">
                Urutan
                <span className="text-red-500">*</span>
                {order >= 0 && (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                )}
              </Label>
              <Input
                id="order"
                type="text"
                inputMode="decimal"
                value={orderInput}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string, numbers, and decimal point
                  if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
                    setOrderInput(value);
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setOrder(numValue);
                    } else if (value === "" || value === "-" || value === ".") {
                      // Allow intermediate states during typing
                      setOrder(0);
                    }
                  }
                }}
                onBlur={(e) => {
                  // Validate and format on blur
                  const value = e.target.value;
                  const numValue = parseFloat(value);
                  if (isNaN(numValue) || numValue < 0) {
                    setOrderInput("0");
                    setOrder(0);
                  } else {
                    // Format to show decimal if needed, otherwise integer
                    if (value.includes(".")) {
                      setOrderInput(value);
                    } else {
                      setOrderInput(numValue.toString());
                    }
                    setOrder(numValue);
                  }
                }}
                placeholder="0.0"
                required
                disabled={isLoading}
                className="h-11 text-base font-mono"
              />
              <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-md">
                <Info className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Angka untuk mengurutkan admin level. Semakin kecil angka, semakin tinggi prioritasnya.
                  Dapat menggunakan desimal (contoh: 1.5, 2.3) untuk penempatan di antara angka bulat.
                </p>
              </div>
            </div>

            {/* Default Privileges */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold flex items-center gap-2">
                Hak Akses Default (Privileges)
                <span className="text-muted-foreground font-normal text-xs">
                  ({selectedPrivileges.length} dipilih)
                </span>
              </Label>
              <div className="space-y-2 p-4 border rounded-lg bg-muted/30">
                {privilegeOptions.map((option) => (
                  <div key={option.value} className="flex items-start gap-3">
                    <Checkbox
                      id={`privilege-${option.value}`}
                      checked={selectedPrivileges.includes(option.value)}
                      onCheckedChange={(checked: boolean | "indeterminate") =>
                        handlePrivilegeToggle(option.value, checked)
                      }
                      disabled={isLoading}
                    />
                    <label
                      htmlFor={`privilege-${option.value}`}
                      className="flex-1 cursor-pointer space-y-1"
                    >
                      <p className="text-sm font-medium leading-none">{option.label}</p>
                      <p className="text-xs text-muted-foreground">{option.description}</p>
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Info className="h-3 w-3" />
                Privileges ini akan menjadi default ketika admin level ini digunakan sebagai template
              </p>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="h-11"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !isFormValid}
              className="h-11 min-w-[120px]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                <>
                  {isEdit ? (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Simpan Perubahan
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-4 w-4" />
                      Tambah Admin Level
                    </>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

